import { DashboardApi } from '@Apis';
import { useDashboardExpandStore } from '@Models/useDashboardExpandModel';
import { useDashboardServicesStore } from '@Models/useDashboardServicesModel';
import { useOrgStore } from '@Models/useOrgModel';
import { useQuery } from '@tanstack/react-query';
import { DashboardPeriod } from '@Types/dashboard';
import { useCallback, useMemo, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useProvisionedServicesQuery } from './useProvisionedServicesQuery';

export const useExpandSection = (props: {
  section: string;
  period: DashboardPeriod;
}) => {
  const { section, period } = props;

  const { orgId } = useOrgStore();
  const servicesModel = useDashboardServicesStore();
  const { sectionsExpanded, handleExpandChange } = useDashboardExpandStore();
  const [searchParams] = useSearchParams();

  const hasManuallyDisabledAllRef = useRef(false);

  const isExpanded = useMemo(() => {
    if (searchParams.get('expand') !== null) return true;
    return sectionsExpanded[section] ?? false;
  }, [searchParams, section, sectionsExpanded]);

  const onExpandChange = useCallback(
    (expanded: boolean) => {
      handleExpandChange(section, expanded);
    },
    [handleExpandChange, section]
  );

  const {
    data: provisionedServices,
    isFetching: provisionedServicesFetching,
    isPending: provisionedServicesPending,
  } = useProvisionedServicesQuery(orgId);

  const servicesQueryLoading = useMemo(() => {
    return provisionedServicesPending || provisionedServicesFetching;
  }, [provisionedServicesFetching, provisionedServicesPending]);

  const { data, isFetching, isPending, isError, refetch } = useQuery({
    queryKey: [
      'dashboardWidget',
      section,
      servicesModel.enabledServices,
      period,
    ],
    queryFn: () =>
      DashboardApi.getDashboardWidget(
        section,
        servicesModel.enabledServices,
        period
      ),
    enabled: servicesModel.hasEnabledProvisionedServices && isExpanded,
    select: (response) => response.data,
  });

  useEffect(() => {
    if (provisionedServices && provisionedServices.length > 0) {
      const hasAnyEnabled = servicesModel.hasEnabledProvisionedServices;

      if (provisionedServices !== undefined) {
        hasManuallyDisabledAllRef.current = !hasAnyEnabled;
      }
    }
  }, [
    provisionedServices,
    provisionedServicesFetching,
    servicesModel.hasEnabledProvisionedServices,
  ]);

  const isLoading = useMemo(() => {
    const hasAnyProvisionedServices =
      provisionedServices && provisionedServices.length > 0;

    if (hasManuallyDisabledAllRef.current) {
      return false;
    }

    const possibleRaceCondition =
      !servicesQueryLoading &&
      hasAnyProvisionedServices &&
      servicesModel.serviceCount === 0; // No services loaded yet in the model

    if (servicesQueryLoading || possibleRaceCondition) return true;

    if (!servicesModel.hasEnabledProvisionedServices) return false;

    if (
      hasAnyProvisionedServices &&
      (isFetching || isPending) // Data for the section is still being fetched
    ) {
      return true;
    }

    return false;
  }, [
    provisionedServices,
    servicesQueryLoading,
    servicesModel,
    isFetching,
    isPending,
  ]);

  return {
    isExpanded,
    onExpandChange,
    data,
    isLoading,
    isError,
    refetch,
  };
};
